import {
  ArrowRightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  MessageOutlined
} from "@ant-design/icons"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { surfaceColor } from "../../constants/surface-tokens"
import { defaultTheme } from "../../theme"
import { startsIn } from "./toastTime"

/**
 * The card an in-app notification toast renders, whatever raised it.
 *
 * It is mounted by antd's static `notification`, which renders outside the
 * app's providers, so it takes plain strings and callbacks and spells its
 * colours and typeface out rather than reading the theme or the store.
 */

export type ToastCategory = "calendar" | "messages"

export interface NotificationToastCardProps {
  category: ToastCategory
  heading: string
  title: string
  /** Secondary facts such as the customer, already labelled. Blanks are dropped. */
  context?: Array<string | null | undefined>
  /** A message preview or similar free text, clamped to two lines. */
  body?: string | null
  when?: string | null
  /** An event's start, for the "Starts in" badge. */
  startsAt?: string | null
  actionLabel?: string | null
  onAction?: () => void
  onClose: () => void
}

const CATEGORY_ICON: Record<ToastCategory, React.ReactNode> = {
  calendar: <CalendarOutlined />,
  messages: <MessageOutlined />
}

const CATEGORY_FALLBACK: Record<ToastCategory, string> = {
  calendar: "Calendar",
  messages: "Messages"
}

const BRAND = "EZBuild"

export function NotificationToastCard({
  category,
  heading,
  title,
  context,
  body,
  when,
  startsAt,
  actionLabel,
  onAction,
  onClose
}: NotificationToastCardProps) {
  const { t } = useTranslation()
  const lines = (context ?? []).filter((line): line is string =>
    Boolean(line?.trim())
  )
  const hasAction = Boolean(actionLabel && onAction)

  return (
    <div
      data-testid="notification-toast-card"
      className="relative flex"
      style={{
        fontFamily: defaultTheme.token?.fontFamily,
        color: surfaceColor.ink
      }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1 bg-[#2A7D8C]"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 py-3.5 pl-5 pr-3.5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-[15px] text-[#2A7D8C]"
            style={{ background: surfaceColor.tealPale }}
          >
            {CATEGORY_ICON[category]}
          </span>
          <span
            className="min-w-0 flex-1 truncate text-[13px]"
            style={{ color: surfaceColor.inkMuted }}
          >
            {BRAND} ·{" "}
            {t(
              `notifications.toast.category.${category}`,
              CATEGORY_FALLBACK[category]
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("notifications.toast.close", "Dismiss notification")}
            className="-mr-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-[14px] text-[#697789] transition-colors outline-none hover:bg-[#EFF3F7] hover:text-[#253348] focus-visible:ring-2 focus-visible:ring-[#2A7D8C]"
          >
            <CloseOutlined aria-hidden />
          </button>
        </div>

        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[16px] leading-[22px] font-bold">
            {heading}
          </span>
          <span
            data-testid="notification-toast-title"
            title={title}
            className="line-clamp-2 text-[14px] leading-5 font-semibold break-words"
          >
            {title}
          </span>
          {lines.map((line) => (
            <span
              key={line}
              title={line}
              className="truncate text-[13px] leading-5"
              style={{ color: surfaceColor.inkMuted }}
            >
              {line}
            </span>
          ))}
          {body?.trim() && (
            <span
              className="line-clamp-2 text-[13px] leading-5 break-words"
              style={{ color: surfaceColor.inkMuted }}
            >
              {body}
            </span>
          )}
        </div>

        {(when || startsAt) && <WhenRow when={when} startsAt={startsAt} />}

        {hasAction && (
          <div
            className="flex justify-end border-t pt-2.5"
            style={{ borderColor: surfaceColor.border }}
          >
            <button
              type="button"
              onClick={onAction}
              className="inline-flex h-8 max-w-full cursor-pointer items-center gap-1.5 rounded-lg bg-[#2A7D8C] px-3.5 text-[13px] font-semibold text-white transition-colors outline-none hover:bg-[#1F5C6B] focus-visible:ring-2 focus-visible:ring-[#2A7D8C] focus-visible:ring-offset-2"
            >
              <span className="truncate">{actionLabel}</span>
              <ArrowRightOutlined aria-hidden className="text-[12px]" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function WhenRow({
  when,
  startsAt
}: {
  when?: string | null
  startsAt?: string | null
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] leading-5"
      style={{ color: surfaceColor.inkMuted }}
    >
      {when && (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <ClockCircleOutlined aria-hidden className="text-[14px]" />
          <span data-testid="notification-toast-when">{when}</span>
        </span>
      )}
      <StartsInBadge startsAt={startsAt} />
    </div>
  )
}

// The card can stay up for the whole countdown, so the badge keeps its own
// clock and drops out the moment the event starts.
function StartsInBadge({ startsAt }: { startsAt?: string | null }) {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!startsAt) return
    const timer = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(timer)
  }, [startsAt])

  const until = startsIn(startsAt, now)
  if (!until) return null

  const label =
    until.unit === "minutes"
      ? t("notifications.toast.startsInMinutes", {
          count: until.count,
          defaultValue: "Starts in {{count}} min"
        })
      : t("notifications.toast.startsInHours", {
          count: until.count,
          defaultValue: "Starts in {{count}} h"
        })

  return (
    <span
      data-testid="notification-toast-starts-in"
      className="rounded-md px-2 py-0.5 text-[12px] leading-[18px] font-semibold"
      style={{
        background: surfaceColor.warningBg,
        color: surfaceColor.atRiskInk
      }}
    >
      {label}
    </span>
  )
}
